/**
 * AuthCallback · v2.6.7 batch J
 *
 * Landing page after the Emergent-managed Google Auth redirect. The Google
 * service appends `#session_id=...` to the redirect URL. We:
 *   1. Parse the session_id out of the URL fragment
 *   2. POST it to /api/auth/google/session (server-to-server lookup)
 *   3. Backend sets the httpOnly session_token cookie + returns the customer
 *   4. CustomerContext is hydrated and the rider is sent to /ride
 *
 * Error paths bounce back to /ride/login with a toast.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomer } from "@/contexts/CustomerContext";
import customerApi from "@/lib/customerApi";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const nav = useNavigate();
  const { setFromGoogle } = useCustomer();
  const ranRef = useRef(false);

  useEffect(() => {
    // useEffect double-fires in React 18 dev StrictMode — guard against the
    // second call so we don't burn the session_id on the wrong POST.
    if (ranRef.current) return;
    ranRef.current = true;

    const fragment = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(fragment);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      // Sometimes browsers strip the fragment if it's already been visited.
      toast.error("Missing session id — please try Google sign-in again");
      nav("/ride/login", { replace: true });
      return;
    }
    customerApi.post("/auth/google/session", {}, {
      headers: { "X-Session-ID": sessionId },
    })
      .then((r) => {
        // Clean the URL fragment so a refresh doesn't re-trigger
        try {
          window.history.replaceState({}, document.title, "/ride");
        } catch (e) {
          // Some embedded webviews block history mutations — non-fatal, user
          // will just see the session_id in the URL until next navigation.
          console.warn("AuthCallback history.replaceState blocked", e?.message);
        }
        // v2.6.9 batch BM-4 · the Google session exchange now returns a JWT
        // alongside the cookie. Stash it so the Bearer auth flow works for
        // cross-origin requests that can't carry credentialed cookies.
        if (r.data?.access_token) {
          try { localStorage.setItem("rtg_customer_token", r.data.access_token); } catch (e) { /* private browsing */ }
        }
        setFromGoogle(r.data?.customer);
        toast.success(`Welcome ${r.data?.customer?.name?.split(" ")[0] || "rider"}!`);
        nav("/ride", { replace: true });
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Google sign-in failed");
        nav("/ride/login", { replace: true });
      });
  }, [nav, setFromGoogle]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-400"/>
        <div className="mt-3 text-sm text-zinc-400">Completing Google sign-in…</div>
      </div>
    </div>
  );
}
